
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom, Observable, of, throwError } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import { ServerCall, Uploadable } from '@akiba/models_beta_temp';
import { environment } from '../../environments/environment';
import { UiService } from './ui.service';
import { LocalHelpers } from '../misc/localHelpers';

@Injectable({
  providedIn: 'platform'
})
export class HTTPRequestService {

  spinnerName = 'primary';
  constructor(private http: HttpClient, private uiService: UiService) {}

  async postRequest(silent: boolean, path: string, data: any): Promise<Uploadable> {

    if (!silent) {
      await this.uiService.showSpinner(this.spinnerName);
    }

    const req = this.http.post<any>(path, data).pipe(take(1), catchError((err) => {
      let errorMsg: string;
      if (err.error instanceof ErrorEvent) {
        errorMsg = `Error: ${err.error.message}`;
      } else {
        errorMsg = err;
      }
      return of([]);
    }));

    const value = await lastValueFrom(req);

    if (!silent) {
      await this.uiService.hideSpinner(this.spinnerName);
    }

    return new Uploadable(value);

  }

  async uploadSilentRequest(type: string, action: string, formData: FormData): Promise<any> {

    const base = environment.useAppEngineLocal ? environment.appEngineURLLocal : environment.appEngineURL;
    const url: string = base + '/' + type + '/' + action;
    const upload = this.http.post<any>(url, formData).pipe(take(1), catchError((err, caught) => {
      return caught;
    }));

    const value = await lastValueFrom(upload);
    return value;

  }

  buildRequest(type: string, action: string, params: any): Observable<ServerCall> {
    this.uiService.showSpinner(this.spinnerName);
    const base = environment.useAppEngineLocal ? environment.appEngineURLLocal : environment.appEngineURL;
    const url: string = base + '/' + type + '/' + action;
    this.uiService.hideSpinner(this.spinnerName);
    return this.http.post<any>(url, params);
  }

  async buildRequestAsync(type: string, action: string, params: any): Promise<ServerCall> {
    await this.uiService.showSpinner(this.spinnerName);
    const base = environment.useAppEngineLocal ? environment.appEngineURLLocal : environment.appEngineURL;
    const url: string = base + '/' + type + '/' + action;
    const req = this.http.post<any>(url, params).pipe(take(1), catchError((err, caught) => {
      return caught;
    }));

    const value = await lastValueFrom(req);
    await this.uiService.hideSpinner(this.spinnerName);
    return new ServerCall(value);
  }

  async buildRawRequestAsync(type: string, action: string, params: any): Promise<any> {
    await this.uiService.showSpinner(this.spinnerName);
    const base = environment.useAppEngineLocal ? environment.appEngineURLLocal : environment.appEngineURL;
    const url: string = base + '/' + type + '/' + action;
    const req = this.http.post<any>(url, params).pipe(take(1), catchError((err) => {
      console.error('Request failed', err);
      return of(undefined);
    }));

    const value = await lastValueFrom(req);
    await this.uiService.hideSpinner(this.spinnerName);
    return value;
  }

  async buildSilentRequestAsync(type: string, action: string, params: any): Promise<ServerCall> {
    const base = environment.useAppEngineLocal ? environment.appEngineURLLocal : environment.appEngineURL;
    const url: string = `${base}/${type}/${action}`;

    const req = this.http.post<any>(url, params).pipe(
      take(1),
      catchError((err) => {
        // Handle the error here
        console.error('Request failed', err);
        // Return an observable that emits an error to stop the retry
        return throwError(() => err);
      })
    );

    try {
      const value = await lastValueFrom(req);
      return new ServerCall(value);
    } catch (error) {
      // Handle the error here if needed
      console.error('Request failed in lastValueFrom', error);
      return new ServerCall({fulfilled: true, successful: false, message: 'Unable to connect'});
    }
  }

}
